import styled, { css } from "styled-components";
import React, { ReactNode } from "react";
import Text from "common/Typography/Text";
import useMeasure from "utils/hooks/useMeasure";
import { phoneMaxWidth } from "common/NewLook/layout.ts";

interface DrawerMyTitleProps {
  leftActions?: ReactNode;
  rightActions?: ReactNode;
  title?: ReactNode;
  titleRowActions?: ReactNode;
}

const DrawerTitleLayout = ({
  leftActions,
  title,
  rightActions,
  titleRowActions,
}: DrawerMyTitleProps) => {
  const hasLeftActions = Boolean(leftActions);
  const hasRightActions = Boolean(rightActions);
  const [measureActionsRef, { width }] = useMeasure<HTMLDivElement>();

  return (
    <div style={{ maxWidth: "100%" }}>
      <div>
        <TitleContent $hasActions={!!titleRowActions} $actionsWidth={width}>
          {title}
        </TitleContent>
        <TitleRowActionsWrapper ref={measureActionsRef}>
          {titleRowActions}
        </TitleRowActionsWrapper>
      </div>
      <ActionsSection
        id="drawer-actions"
        $hasLeftActions={hasLeftActions}
        $hasRightActions={hasRightActions}
      >
        {hasLeftActions && <ActionWrapper>{leftActions}</ActionWrapper>}
        {hasRightActions && <ActionWrapper>{rightActions}</ActionWrapper>}
      </ActionsSection>
    </div>
  );
};

const CLOSE_WIDTH = 40;

const TitleContent = styled.div<{
  $hasActions: boolean;
  $actionsWidth?: number;
}>`
  text-align: center;
  color: ${(p) => p.theme.colors.primaryText};
  padding: 0
    ${(p) => {
      if (p.$actionsWidth) return CLOSE_WIDTH + p.$actionsWidth;
      return p.$hasActions ? 40 : 30;
    }}px;

  ${Text} {
    color: ${(p) => p.theme.colors.primaryText};
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* Left-aligned like a document title, clear of the close button. */
      text-align: start;
      padding-left: 0;
      @media (max-width: ${phoneMaxWidth}) {
        padding-right: ${CLOSE_WIDTH + (p.$actionsWidth ?? 0)}px;
      }
      color: ${p.theme.colors.titleColor};
      font-size: 16px;
      font-weight: ${p.theme.typography.fontWeights.SEMI_BOLD};
      > * {
        justify-content: flex-start !important;
      }
      ${Text} {
        color: ${p.theme.colors.titleColor};
        font-size: 16px;
        font-weight: ${p.theme.typography.fontWeights.SEMI_BOLD};
      }
    `}
`;
const TitleRowActionsWrapper = styled.div`
  position: absolute;
  right: 42px;
  top: 8px;
`;
const ActionWrapper = styled.div`
  display: flex;
  column-gap: 8px;
`;

const ActionsSection = styled.div<{
  $hasLeftActions: boolean;
  $hasRightActions: boolean;
}>`
  display: flex;
  justify-content: space-between;
  flex-direction: ${(p) => (p.$hasLeftActions ? "row" : "row-reverse")};
  margin-top: 8px;
  svg {
    color: ${(p) => p.theme.colors.textColor};
  }

  ${(p) =>
    !p.$hasRightActions || !p.$hasLeftActions
      ? `> div {
  width: 100%;
  }`
      : ""};

  @media (max-width: ${phoneMaxWidth}) {
    margin: 8px 0;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      margin-top: 12px;
      svg {
        color: ${p.theme.colors.secondaryText};
      }
    `}
`;

export default DrawerTitleLayout;
